import React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/format";
import { resolveMediaUrl } from "@/lib/api";
import { useTour } from "@/help/TourProvider";

export function Button({
  children,
  variant = "primary",
  size = "md",
  loading,
  disabled,
  className,
  testID,
  ...props
}) {
  const base =
    "inline-flex items-center justify-center gap-2 font-sans font-medium rounded-pill transition-all duration-200 active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none select-none";
  const variants = {
    primary: "bg-brand text-white hover:brightness-105 shadow-sm",
    secondary: "bg-surface-3 text-ink hover:bg-line",
    ghost: "bg-transparent text-ink hover:bg-surface-3",
    outline: "bg-transparent text-ink border border-ink hover:bg-ink hover:text-surface",
    dark: "bg-ink text-surface hover:brightness-125",
  };
  const sizes = {
    sm: "text-[13px] px-4 py-2",
    md: "text-[15px] px-5 py-2.5",
    lg: "text-base px-6 py-3",
  };
  return (
    <button
      className={cn(base, variants[variant], sizes[size], className)}
      disabled={disabled || loading}
      data-testid={testID}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

export function Input({ label, className, containerClassName, type, ...props }) {
  const noCheck = ["email", "password", "url", "tel", "number", "search"].includes(type);
  return (
    <label className={cn("flex flex-col gap-1.5", containerClassName)}>
      {label && <span className="text-[13px] text-muted-foreground font-sans">{label}</span>}
      <input
        type={type}
        spellCheck={noCheck ? false : (props.spellCheck ?? true)}
        autoCapitalize={noCheck ? "none" : props.autoCapitalize}
        className={cn(
          "bg-surface-2 text-ink rounded-md px-4 py-3 font-sans text-[15px] border border-line",
          "outline-none focus:border-brand transition-colors placeholder:text-muted-foreground/70",
          className
        )}
        {...props}
      />
    </label>
  );
}

export function Textarea({ label, className, ...props }) {
  return (
    <label className="flex flex-col gap-1.5">
      {label && <span className="text-[13px] text-muted-foreground font-sans">{label}</span>}
      <textarea
        spellCheck={props.spellCheck ?? true}
        className={cn(
          "bg-surface-2 text-ink rounded-md px-4 py-3 font-sans text-[15px] border border-line",
          "outline-none focus:border-brand transition-colors resize-y min-h-[96px] placeholder:text-muted-foreground/70",
          className
        )}
        {...props}
      />
    </label>
  );
}

export function Avatar({ uri, name, size = 44, className, online }) {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [uri]);
  const src = failed ? null : resolveMediaUrl(uri);
  const dot = online != null && (
    <span
      title={online ? "Online" : "Offline"}
      style={{ width: Math.max(10, size * 0.28), height: Math.max(10, size * 0.28) }}
      className={cn("absolute bottom-0 right-0 rounded-full border-2 border-surface-2", online ? "bg-emerald-500" : "bg-stone-300")}
    />
  );
  const isOfficial = !src && (name === "Two Degrees" || name === "Two Degrees Team");
  const inner = src ? (
    <img src={src} alt={name || ""} style={{ width: size, height: size }} onError={() => setFailed(true)}
      className="rounded-full object-cover bg-surface-3 shrink-0" />
  ) : isOfficial ? (
    <img src="/brand/badge-3.png" alt="Two Degrees" style={{ width: size, height: size }}
      className="rounded-full object-cover bg-surface-3 shrink-0" />
  ) : (
    <div style={{ width: size, height: size }}
      className="rounded-full bg-brand-2 flex items-center justify-center shrink-0">
      <span className="font-serif text-ink" style={{ fontSize: size * 0.36 }}>{initials(name)}</span>
    </div>
  );
  if (online == null) return <div className={cn("shrink-0", className)}>{inner}</div>;
  return <div className={cn("relative shrink-0", className)}>{inner}{dot}</div>;
}

export function Chip({ label, active, onClick, testID }) {
  return (
    <button
      data-testid={testID}
      onClick={onClick}
      className={cn(
        "h-9 px-4 rounded-pill text-[13px] font-sans whitespace-nowrap border transition-all shrink-0",
        active
          ? "bg-ink text-surface border-ink"
          : "bg-surface-2 text-ink border-line hover:border-ink/40"
      )}
    >
      {label}
    </button>
  );
}

export function Tag({ label, className }) {
  return (
    <span
      className={cn(
        "px-3 py-1 rounded-pill bg-brand-tint text-brand-tint-ink text-[11px] font-sans",
        className
      )}
    >
      {label}
    </span>
  );
}

export function Card({ children, className, onClick, testID }) {
  return (
    <div
      data-testid={testID}
      onClick={onClick}
      className={cn(
        "bg-surface-2 rounded-lg p-5 border border-line",
        onClick && "cursor-pointer hover:border-ink/30 transition-colors",
        className
      )}
    >
      {children}
    </div>
  );
}

export function Loading({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-brand" />
      {label && <span className="text-muted-foreground font-sans text-sm">{label}</span>}
    </div>
  );
}

export function EmptyState({ title, subtitle, action, tour }) {
  const t = useTour();
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 gap-2 text-center">
      <h3 className="font-serif text-xl text-ink">{title}</h3>
      {subtitle && <p className="text-muted-foreground text-sm max-w-xs font-sans">{subtitle}</p>}
      {action && <div className="mt-3">{action}</div>}
      {tour && t?.start && (
        <button
          data-testid={`tour-nudge-${tour}`}
          onClick={() => t.start(tour)}
          className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-brand hover:text-brand/80 bg-brand/10 hover:bg-brand/15 rounded-full px-3.5 py-1.5 transition-colors"
        >
          New here? Take a quick tour
        </button>
      )}
    </div>
  );
}

export function Divider({ className }) {
  return <div className={cn("h-px bg-divider", className)} />;
}

export function IconText({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground text-[13px] font-sans">
      <Icon className="w-3.5 h-3.5" />
      {text}
    </span>
  );
}

export function PageTitle({ children, sub }) {
  return (
    <div className="mb-6">
      <h1 className="font-serif text-4xl sm:text-5xl text-ink leading-tight">{children}</h1>
      {sub && <p className="text-muted-foreground font-sans mt-1">{sub}</p>}
    </div>
  );
}

/** Compact rating badge shown next to a verified check. Renders nothing until rated. */
export function Rating({ avg, count, className }) {
  if (!count) return null;
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-[12px] text-ink", className)} title={`${avg} from ${count} rating${count > 1 ? "s" : ""}`}>
      <span className="text-brand">★</span>
      <span className="font-sans font-medium">{Number(avg).toFixed(1)}</span>
      <span className="text-muted-foreground">({count})</span>
    </span>
  );
}

/** Interactive 1-5 star picker. */
export function StarPicker({ value, onChange, size = 28 }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`star-${n}`}
          onClick={() => onChange(n)}
          style={{ fontSize: size }}
          className={cn("leading-none transition-transform hover:scale-110", n <= value ? "text-brand" : "text-line")}
        >
          ★
        </button>
      ))}
    </div>
  );
}
